import { NotificationsApi } from "@genesys/cloud-frontend/apis/NotificationsApi";
import LOG from "services/loggingService";
import pubSub from "util/PubSub";
import { API_CONFIGURATION } from "util/apiUtils";
import ExponentialIntervalGenerator from "util/ExponentialIntervalGenerator";

const HEARTBEAT_EXPIRATION_MS = 35000;
const SUBSCRIPTION_DEBOUNCE_MS = 200;
const MAX_RECONNECT_INTERVAL_MS = 30000;
// Give up reconnecting after 30 minutes without a successful connection
const RECONNECT_WINDOW_MS = 30 * 60 * 1000;

type NotificationMessage = {
    topicName: string;
    eventBody: unknown;
};

type Subscription = {
    topicId: string;
    callback: (eventBody: unknown) => void;
};

class NotificationService {
    private channelId?: string;
    private connectUri?: string;
    private connection?: WebSocket;
    private subscriptions: Subscription[] = [];
    private heartbeatTimer?: ReturnType<typeof setTimeout>;
    private reconnectTimer?: ReturnType<typeof setTimeout>;
    private subscriptionDebounceTimer?: ReturnType<typeof setTimeout>;
    private lastConnectionTime?: number;
    private setupPromise?: Promise<void>;
    private backoff = new ExponentialIntervalGenerator(
        MAX_RECONNECT_INTERVAL_MS
    );

    /**
     * Subscribes to a hawk topic. The notification channel and WebSocket are
     * created lazily on the first subscription, and torn down when the last
     * subscription is removed (via the function returned here).
     *
     * @returns an unsubscribe function
     */
    subscribe(topicId: string, callback: (eventBody: unknown) => void) {
        this.subscriptions.push({ topicId, callback });
        pubSub.Topic(topicId)?.subscribe(callback);

        // Connect whenever there is no live channel (also re-arms after a
        // reconnect give-up)
        if (!this.channelId && !this.setupPromise) {
            this.setupConnection();
        } else {
            this.debouncedSyncSubscriptions();
        }

        return () => this.unsubscribe(topicId, callback);
    }

    private unsubscribe(
        topicId: string,
        callback: (eventBody: unknown) => void
    ) {
        this.subscriptions = this.subscriptions.filter(
            (s) => !(s.topicId === topicId && s.callback === callback)
        );
        pubSub.Topic(topicId)?.unsubscribe(callback);

        if (this.subscriptions.length === 0) {
            this.stop();
        } else {
            this.debouncedSyncSubscriptions();
        }
    }

    private async setupConnection(): Promise<void> {
        if (this.setupPromise) {
            return this.setupPromise;
        }

        this.setupPromise = (async () => {
            try {
                const channel = await new NotificationsApi(
                    API_CONFIGURATION
                ).postNotificationsChannels({});
                if (!channel.id || !channel.connectUri) {
                    throw new Error(
                        `Unexpected notification channel response: ${JSON.stringify(
                            channel
                        )}`
                    );
                }
                // Last subscriber may have left while the channel was created
                if (this.subscriptions.length === 0) {
                    return;
                }
                this.channelId = channel.id;
                this.connectUri = channel.connectUri;
                LOG.info("WS: notification channel created", this.channelId);
                this.connect();
            } catch (e) {
                LOG.error("Unable to create notification channel", e);
                this.scheduleReconnect();
            } finally {
                this.setupPromise = undefined;
            }
        })();

        return this.setupPromise;
    }

    private connect = () => {
        if (!this.connectUri) {
            return;
        }

        this.connection = new WebSocket(this.connectUri);

        this.connection.onopen = () => {
            LOG.debug("WS: open");
            this.backoff.reset();
            this.lastConnectionTime = Date.now();
            this.resetHeartbeatTimer();
            this.syncSubscriptions();
        };

        this.connection.onmessage = (messageEvent: MessageEvent) => {
            if (!messageEvent.data) {
                return;
            }

            let json: NotificationMessage;
            try {
                json = JSON.parse(messageEvent.data);
            } catch (e) {
                LOG.error("WS: unable to parse message", e);
                return;
            }

            if (json.topicName === "channel.metadata") {
                this.resetHeartbeatTimer();
                return;
            }

            LOG.info("WS: event received", json.topicName, json.eventBody);
            pubSub.Topic(json.topicName)?.publish(json.eventBody);
        };

        this.connection.onclose = () => {
            this.clearHeartbeatTimer();
            if (this.subscriptions.length > 0) {
                this.scheduleReconnect();
            }
        };

        this.connection.onerror = (e) => {
            LOG.error("WS: error", e);
            this.connection?.close();
        };
    };

    private scheduleReconnect() {
        const withinReconnectWindow =
            !this.lastConnectionTime ||
            Date.now() - this.lastConnectionTime < RECONNECT_WINDOW_MS;

        if (!withinReconnectWindow) {
            LOG.warn(
                "WS: pausing reconnection attempts, reconnect window expired"
            );
            // Drop the dead channel so a later subscribe starts fresh
            this.channelId = undefined;
            this.connectUri = undefined;
            return;
        }

        const interval = this.backoff.getInterval();
        this.backoff.fail();
        this.reconnectTimer = setTimeout(() => {
            // The channel is tied to the dead socket; create a fresh one
            this.channelId = undefined;
            this.connectUri = undefined;
            this.setupConnection();
        }, interval);
    }

    private resetHeartbeatTimer() {
        this.clearHeartbeatTimer();
        this.heartbeatTimer = setTimeout(() => {
            LOG.warn("WS: heartbeat expired, closing connection");
            this.connection?.close();
        }, HEARTBEAT_EXPIRATION_MS);
    }

    private clearHeartbeatTimer() {
        if (this.heartbeatTimer) {
            clearTimeout(this.heartbeatTimer);
            this.heartbeatTimer = undefined;
        }
    }

    private debouncedSyncSubscriptions() {
        if (this.subscriptionDebounceTimer) {
            clearTimeout(this.subscriptionDebounceTimer);
        }
        this.subscriptionDebounceTimer = setTimeout(
            () => this.syncSubscriptions(),
            SUBSCRIPTION_DEBOUNCE_MS
        );
    }

    private async syncSubscriptions() {
        if (!this.channelId || this.subscriptions.length === 0) {
            return;
        }

        const topicIds = [
            ...new Set(this.subscriptions.map((s) => s.topicId)),
        ].map((id) => ({ id }));

        try {
            await new NotificationsApi(
                API_CONFIGURATION
            ).putNotificationsChannelSubscriptions({
                channelId: this.channelId,
                body: topicIds,
            });
            LOG.debug("WS: subscriptions registered", topicIds);
        } catch (e) {
            LOG.error("Unable to update notification subscriptions", e);
        }
    }

    /**
     * Tears down the WebSocket and channel state. Called automatically when
     * the last subscription is removed.
     */
    stop() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = undefined;
        }
        if (this.subscriptionDebounceTimer) {
            clearTimeout(this.subscriptionDebounceTimer);
            this.subscriptionDebounceTimer = undefined;
        }
        this.clearHeartbeatTimer();

        // Detach handlers so a late onclose doesn't schedule a reconnect
        if (this.connection) {
            this.connection.onclose = null;
            this.connection.onmessage = null;
            this.connection.onopen = null;
            this.connection.onerror = null;
            this.connection.close();
            this.connection = undefined;
        }

        this.channelId = undefined;
        this.connectUri = undefined;
        // Reset so a later mount gets a fresh reconnect window
        this.lastConnectionTime = undefined;
    }
}

export default new NotificationService();
